const digitsOnly = (value: string) => value.replace(/\D/g, "");

export const normalizeCardNumber = (value: string) =>
  digitsOnly(value)
    .slice(0, 19)
    .replace(/(.{4})/g, "$1 ")
    .trim();

export const normalizeCardExpiry = (value: string) => {
  const digits = digitsOnly(value).slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

export const parseCardExpiry = (
  value: string,
  now: Date = new Date(),
): { month: string; year: string } | null => {
  const [month, year] = value.split("/").map((part) => part?.trim() ?? "");
  if (!/^\d{2}$/.test(month) || !/^\d{2}$/.test(year)) return null;
  const monthNumber = Number.parseInt(month, 10);
  const yearNumber = Number.parseInt(year, 10);
  if (monthNumber < 1 || monthNumber > 12) return null;

  const currentYear = now.getFullYear() % 100;
  const currentMonth = now.getMonth() + 1;
  if (
    yearNumber < currentYear ||
    (yearNumber === currentYear && monthNumber < currentMonth)
  ) {
    return null;
  }
  return { month, year };
};

export const isValidCardNumber = (value: string): boolean => {
  const digits = digitsOnly(value);
  if (!/^\d{12,19}$/.test(digits) || /^0+$/.test(digits)) return false;

  let sum = 0;
  let double = false;
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    let digit = Number.parseInt(digits[index] ?? "", 10);
    if (double) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    double = !double;
  }
  return sum % 10 === 0;
};

export const getCardDraftValidationError = (input: {
  name: string;
  number: string;
  expiry: string;
  cvv: string;
  now?: Date;
}): {
  reason: string;
  message: string;
  field: "name" | "number" | "expiry" | "cvv";
} | null => {
  if (!input.name.trim()) {
    return {
      reason: "missing_cardholder_name",
      field: "name",
      message: "Enter the cardholder name.",
    };
  }
  if (!isValidCardNumber(input.number)) {
    return {
      reason: "invalid_card_number",
      field: "number",
      message:
        "Check the card number and enter all its digits. No payment has been submitted.",
    };
  }
  if (!parseCardExpiry(input.expiry, input.now)) {
    return {
      reason: "invalid_card_expiry",
      field: "expiry",
      message:
        "Enter the expiry date as MM/YY. If your card has expired, use another card.",
    };
  }
  if (!/^\d{3,4}$/.test(input.cvv)) {
    return {
      reason: "invalid_card_cvv",
      field: "cvv",
      message: "Enter the 3 or 4 digit security code printed on your card.",
    };
  }
  return null;
};
